import { useRef } from 'react';
import { useTranslation } from '@klv/hooks/useTranslation';
import { debounce } from 'lodash-es';

import { useSearchBox } from 'react-instantsearch';

import { generateClassName, windowIsDefined } from '@klv/shared/utils/functions';

import Icon from '@klv/atoms/icon';
import useIsSearchPage from '@klv/hooks/useIsSearchPage';

import * as styles from './styles/searchBox.module.scss';
import type { ISearchBox } from './types';
import type { FormEvent } from 'react';

// for use in server rendering to prevent layout shifts
export const StaticSearchBox = ({ className, placeholder }: Pick<ISearchBox, 'className' | 'placeholder'>) => {
  const { t } = useTranslation();

  return (
    <div className={generateClassName([styles.styledSearchBox, className])}>
      <Icon className={styles.searchIcon} color='currentColor' iconName='Search' />
      <form>
        <input
          className={styles.searchInputStyles}
          autoCapitalize='none'
          autoComplete='off'
          autoCorrect='off'
          id='search'
          name='query'
          placeholder={placeholder ? placeholder : t('molecules_searchBox_placeholder')}
          spellCheck='false'
          type='text'
        />
        <input type='submit' hidden />
      </form>
    </div>
  );
};

const SearchBox = ({ 
  className,
  indexName,
  instantResults, 
  placeholder,
  searchPage = '/search',
  firstResource,
  selectFacets = [],
}: ISearchBox) => {
  const { t } = useTranslation();
  const locale = t('langCodes_localeLowerCase');

  const { query, refine } = useSearchBox();
  const searchBox = useRef<HTMLInputElement>(null);
  
  const submitForm = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (windowIsDefined()) {
      
      const basePath = indexName === 'help_center_articles' ? `/hc/${locale}${searchPage}` : `${t('langCodes_pathPrefix') !== '' ? `/${t('langCodes_pathPrefix')}` : ''}/search-all`;
      const url = new URL(window.location.origin + basePath);
      
      if (firstResource) {
        url.searchParams.set('resource', 'marketing_sites');
      }
      if (selectFacets.length > 0) {
        url.searchParams.set('select',  selectFacets.join('|'));
      } 

      url.searchParams.set('query', query);
      window.location.href = url.toString();
    }
  };

  const clearForm = () => {
    if (searchBox?.current?.value) {
      searchBox.current.value = '';
    }
    refine('');
  };

  const handleChange = debounce((evt: React.ChangeEvent<HTMLInputElement>) => {
    refine(evt.target.value);
  }, 250);

  const isSearchPage = useIsSearchPage();

  return (
    <div className={generateClassName([styles.styledSearchBox, className])}>
      <Icon className={styles.searchIcon} color='currentColor' iconName='Search' />
      <form onSubmit={submitForm}>
        <input
          className={styles.searchInputStyles}
          ref={searchBox}
          autoCapitalize='none'
          autoComplete='off'
          autoCorrect='off'
          defaultValue={query}
          id='search'
          name='query'
          placeholder={placeholder ? placeholder : t('molecules_searchBox_placeholder')}
          spellCheck='false'
          type='text'
          onChange={handleChange}
        />
        <input type='submit' hidden />
        {query && (
          <button className={styles.resetButton} type="button" onClick={clearForm}>
            <Icon iconName="X"/>
          </button>
        )}
      </form>
      {!!query && !isSearchPage && (
        <div>
          {instantResults}
        </div>
      )}
    </div>
  );
};

export default SearchBox;
